import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import TierBadge from '../components/TierBadge.jsx'
import { LOADING, SIGNED_IN, useSession } from '../session.js'
import { createTrip, listTrips } from '../trips/api.js'
import { ONLY_YOU, onlyYouCanSee } from '../trips/people.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// Your trips. P5-3 T3 shipped this as an empty state and nothing else; T4 makes
// it real and keeps the empty state exactly as it was worded, because it is
// still the right sentence for somebody with no trips.
//
// The three session states below are not a permissions model — that comment
// from T3 still stands. A hard navigation here never reaches the signed-out
// branch, because functions/_middleware.js redirects it to sign in before the
// document is served. The branch exists for a client-side navigation and for a
// session that expired while the tab was open.
//
// ⚠️ Starting a trip is here rather than on the board, and it is the smallest
// form that produces a valid trip: §5.1 is "title, display currency, invitees",
// and invitees are omitted.
//
// 🔴 THIS COMMENT SAID THE OMISSION MADE IT "a crew trip the whole server can
// see, which is what a six-person server wants by default". **THAT WAS FALSE,
// measured against the real service on 2026-08-22 (P6-81).** `TripCreate`
// defaults `visibility` to `invitees` and functions/api/trips/index.js
// forwards exactly that, so a trip started here is invitee-scoped with an
// EMPTY list — and `role_for` gives everybody who is not the organiser NONE.
// Every trip ever started on this site has been visible to its organiser and
// to nobody else. `trips/tests/test_invitees.py` drives it rather than
// describing it.
//
// The default is deliberately left alone rather than quietly flipped: what was
// missing was not a different default but a SURFACE, and P6-81 built it.
// /trips/:tripId/people is where somebody says who is on the trip and who can
// see it, and the board's "People · just you" link is what makes the state
// above visible from the trip it applies to.
//
// ✅ AND JORDAN HAS SINCE RULED ON THE DEFAULT RATHER THAN LEAVING IT ALONE BY
// OMISSION (2026-08-22, board row P6-92): *"there are 6 main members but
// numerous people in the server, so no I do not want it just open."* `crew`
// means every signed-in server member, and the server is much larger than the
// group that plans trips. So organiser-only is the INTENDED starting state and
// nothing on this page may read as a mistake somebody made.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 P6-93: THE LIST IS WHERE "AT A GLANCE" HAS TO HAPPEN
// ═══════════════════════════════════════════════════════════════════════════
//
// P6-81's link says it on the board, one trip at a time. An organiser with
// three trips nobody can see had to open all three to find that out — and the
// board's label is a HEADCOUNT ("People · just you"), which is a different
// sentence from "nobody else can see this" and can be read without learning
// it. So the fact gets said here, plainly, on every row it is true of.
//
// 🔴 The predicate is NOT `headcount === 1` — see `src/trips/people.js`, which
// owns it. A crew trip with no invitees has a headcount of one and the whole
// server can open it; marking that row would be telling somebody something
// false about who can see their trip.
//
// ⚠️ It is a fact and not a call to action: no warn styling, and it links
// nowhere. The trip title is one click from the board, which carries the same
// fact beside the door that changes it. A second link on every row would make
// the list harder to scan in order to shave a hop off a thing that is not
// broken.

export default function Trips({ section }) {
  useDocumentTitle(section.title)
  const session = useSession()
  const signedIn = session.status === SIGNED_IN

  const [state, setState] = useState({ status: 'idle', trips: [], error: null })
  const [starting, setStarting] = useState(false)
  const [title, setTitle] = useState('')
  const [currency, setCurrency] = useState('GBP')
  const [formError, setFormError] = useState(null)

  const load = useCallback(async () => {
    try {
      const trips = await listTrips()
      setState({ status: 'ready', trips, error: null })
    } catch (error) {
      setState({ status: 'error', trips: [], error: error.message })
    }
  }, [])

  useEffect(() => {
    // 🔴 Nothing is asked for until the session says there is somebody to ask
    // for. A signed-out browser hitting /api/trips would get an honest 401, but
    // it would be a request made on behalf of nobody — and the failure would
    // render as "the trips service is unreachable", which is a different and
    // untrue sentence.
    if (!signedIn) return undefined
    let cancelled = false
    listTrips().then(
      (trips) => {
        if (!cancelled) setState({ status: 'ready', trips, error: null })
      },
      (error) => {
        if (!cancelled) setState({ status: 'error', trips: [], error: error.message })
      },
    )
    return () => {
      cancelled = true
    }
  }, [signedIn])

  const start = async (event) => {
    event.preventDefault()
    if (title.trim() === '') return
    setStarting(true)
    setFormError(null)
    try {
      await createTrip({ title: title.trim(), display_currency: currency.trim().toUpperCase() })
      setTitle('')
      await load()
    } catch (error) {
      setFormError(error.message)
    } finally {
      setStarting(false)
    }
  }

  return (
    <article className="d2d-page">
      <header className="d2d-titleblock">
        <p className="d2d-eyebrow d2d-micro">
          <TierBadge tier={section.tier} />
        </p>
        <h1 className="d2d-titleblock__headline">{section.title}</h1>
        <p className="d2d-titleblock__lede">{section.summary}</p>
        <hr className="d2d-rule" />
      </header>

      {session.status === LOADING ? (
        <p className="d2d-notice" data-testid="trips-loading">
          Checking whether you are signed in.
        </p>
      ) : !signedIn ? (
        <p className="d2d-notice" data-testid="trips-signed-out">
          You need to be signed in to see this.{' '}
          <a href="/auth/login?next=%2Ftrips">Sign in with Discord</a>.
        </p>
      ) : state.status === 'error' ? (
        <p className="d2d-notice" data-testid="trips-error">
          {state.error}
        </p>
      ) : state.status === 'idle' ? (
        <p className="d2d-notice" data-testid="trips-loading-list">
          Loading your trips.
        </p>
      ) : (
        <>
          {state.trips.length === 0 ? (
            <p className="d2d-notice" data-testid="trips-empty">
              No trips yet. Somebody has to start one.
            </p>
          ) : (
            <ul className="d2d-triplist" data-testid="trips-list">
              {state.trips.map((trip) => (
                <li key={trip.id} className="d2d-triplist__item">
                  <Link className="d2d-triplist__link" to={`/trips/${trip.id}`}>
                    {trip.title}
                  </Link>
                  <span className="d2d-triplist__meta d2d-micro">
                    {trip.headcount} {trip.headcount === 1 ? 'person' : 'people'} · {trip.state}
                    {onlyYouCanSee(trip.visibility, trip.headcount) ? (
                      <>
                        {' · '}
                        <span
                          className="d2d-triplist__visibility"
                          data-testid={`trip-only-you-${trip.id}`}
                        >
                          {ONLY_YOU}
                        </span>
                      </>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <form className="d2d-startTrip" onSubmit={start} aria-label="Start a trip">
            <p className="d2d-startTrip__field">
              <label className="d2d-micro" htmlFor="new-trip-title">
                Start a trip
              </label>
              <input
                id="new-trip-title"
                value={title}
                maxLength={200}
                required
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Portugal, the big one"
              />
            </p>
            <p className="d2d-startTrip__field">
              <label className="d2d-micro" htmlFor="new-trip-currency">
                Currency
              </label>
              <input
                id="new-trip-currency"
                value={currency}
                maxLength={3}
                required
                onChange={(event) => setCurrency(event.target.value)}
              />
            </p>
            <button type="submit" className="d2d-startTrip__submit" disabled={starting}>
              {starting ? 'Starting…' : 'Start it'}
            </button>
          </form>
          {formError ? (
            <p className="d2d-notice d2d-notice--warn" role="status" data-testid="trips-form-error">
              {formError}
            </p>
          ) : null}
        </>
      )}
    </article>
  )
}
